import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import { Alert, Button, Chip, Container, Stack, TextField } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRowParams } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageHeader from '~/components/Common/Elements/PageHeader';

import { SEARCH_DEBOUNCE_MS } from '~/consts/tours';

import { useGetBundles } from '~/hooks/apis/bundle/useBundles';
import useDebounced from '~/hooks/useDebounced';

import { formatDateLongMonthWithMeridiemNoSeconds } from '~/services/TimeService';

import { BUNDLE_STATUS_LABELS, BundleSummary } from '~/types/bundles/bundle';

import CreateBundleDialog from '../../Bundles/CreateBundleDialog';
import { bundlePath } from '../../nativeTourModel';

const PAGE_SIZE = 20;

const columns: Array<GridColDef<BundleSummary>> = [
  {
    field: 'name',
    headerName: 'Name',
    sortable: false,
    flex: 2,
    display: 'flex',
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: false,
    width: 140,
    display: 'flex',
    renderCell: ({ row }) => (
      <Chip
        size="small"
        label={BUNDLE_STATUS_LABELS[row.status]}
        color={row.status === 'approved' ? 'success' : 'default'}
      />
    ),
  },
  {
    field: 'updated_at',
    headerName: 'Last updated',
    sortable: false,
    flex: 1,
    display: 'flex',
    valueFormatter: (value: string) => formatDateLongMonthWithMeridiemNoSeconds(value),
  },
];

export default function TourBundlesPage() {
  const navigate = useNavigate();
  const [filterInput, setFilterInput] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: PAGE_SIZE });
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const filter = useDebounced(filterInput.trim(), SEARCH_DEBOUNCE_MS);

  const { data, isFetching, error } = useGetBundles({
    filter: filter || undefined,
    offset: paginationModel.page * paginationModel.pageSize,
    limit: paginationModel.pageSize,
    sort_by: 'updated_at',
    sort_order: 'desc',
  });

  const handleFilterChange = (value: string) => {
    setFilterInput(value);
    setPaginationModel((model) => ({ ...model, page: 0 }));
  };

  return (
    <Container>
      <PageHeader title="Bundles">
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsCreateOpen(true)}>
          Create bundle
        </Button>
      </PageHeader>

      <Stack spacing={2}>
        <TextField
          label="Search by name"
          value={filterInput}
          onChange={(event) => handleFilterChange(event.target.value)}
        />

        {!!error && <Alert severity="error">{error.message}</Alert>}

        {!error && (
          <DataGrid
            rows={data?.bundles ?? []}
            rowCount={data?.pagination.total_items ?? 0}
            columns={columns}
            loading={isFetching}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[PAGE_SIZE]}
            onRowClick={(params: GridRowParams<BundleSummary>) => navigate(bundlePath(params.row.id))}
            slots={{ pagination: GridPagination }}
            localeText={{ noRowsLabel: filter ? `No bundles match "${filter}".` : 'No bundles yet.' }}
            disableColumnMenu
            disableColumnFilter
            disableRowSelectionOnClick
            autoHeight
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        )}
      </Stack>

      <CreateBundleDialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
    </Container>
  );
}
